// Persistent, global debug log for beta diagnostics. When enabled (default ON), it records every raw
// LLM exchange (chat / regenerate / continue / impersonate / summary / test connection) plus errors,
// with API keys redacted, so a tester can export a single file and send it back.
//
// Durability: entries live in a capped in-memory ring AND are mirrored to the encrypted vault
// (debounced) so they survive a reload/crash. Load happens on boot; the exchange sink is registered
// once. Exchanges arrive already redacted from the inference layer (headers + url); we additionally
// scrub free-text error details defensively.
import { onExchangeComplete, setExchangeContext, type RawExchange } from "@aicc/inference";
import { saveExport, settingsGet, settingsSet } from "./api";
import { isRemote } from "./rpc";

export type DebugKind = "llm" | "error" | "info";
export interface DebugEntry {
  t: number; // epoch ms
  kind: DebugKind;
  /** Operation label: "chat", "regenerate", "continue", "impersonate", "summary", "first-message",
   *  "test", "error", etc. LLM entries inherit the current debug context. */
  label: string;
  detail?: string; // error text / note (redacted)
  exchange?: RawExchange; // redacted raw request/response for kind === "llm"
}

const ENABLED_KEY = "debug_enabled";
const ENTRIES_KEY = "debug_entries";
const CAP = 300; // ring size — newest CAP entries are kept
/**
 * Byte budget for the persisted ring, and the reason it exists: CAP counts ENTRIES, and an entry is a
 * whole raw exchange — system prompt, full chat history, card, worldbook, plus the reply. On a long chat
 * that is ~75KB each, so 300 of them is a 22.9 MB settings row, read on every unlock (measured at 4.25s,
 * with the whole command queue stuck behind it). Whichever limit bites first wins.
 *
 * 1 MB, because a report needs what happened just BEFORE the thing being reported, not a week of traffic:
 * measured against a real vault (300 entries, 22.9 MB) that is ~13 worst-case exchanges and far more
 * ordinary ones. Safe to shrink only because the error reserve is now persisted separately — traffic can
 * no longer push a crash out of the file (see ERROR_CAP).
 */
const MAX_BYTES = 1024 * 1024;
/** And nothing persists longer than a day. A bug report is about what just happened; last week's traffic
 *  is only weight on every unlock. Enforced by age rather than by a scheduled purge so it holds however
 *  the app was closed — there is no "once a day" moment to miss. */
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

/** Drop entries until the ring fits ALL THREE limits: age, count, bytes. Errors are also held in their
 *  own reserve (errorEntries), so trimming traffic here cannot silently discard the crash being
 *  reported. `now` is injectable so the age rule is testable without touching the clock. */
function trimToBudget(list: DebugEntry[], now = Date.now()): DebugEntry[] {
  const fresh = list.filter((e) => typeof e?.t !== "number" || now - e.t <= MAX_AGE_MS);
  // An entry with no usable timestamp is kept rather than assumed stale — losing a report to a missing
  // field is worse than carrying a few odd rows, and the byte budget below still bounds them.
  list = fresh;
  if (!list.length) return [];
  if (list.length > CAP) list = list.slice(-CAP);
  // Size each entry once, from the back, rather than re-stringifying the whole array per drop.
  let total = 0;
  let keepFrom = 0;
  for (let i = list.length - 1; i >= 0; i--) {
    total += JSON.stringify(list[i]).length;
    if (total > MAX_BYTES) { keepFrom = i + 1; break; }
  }
  // Never return nothing: one entry over budget on its own is still the most recent thing that happened.
  return keepFrom >= list.length ? list.slice(-1) : list.slice(keepFrom);
}

/**
 * Errors are ALSO kept in their own ring, and this is why.
 *
 * One shared FIFO meant a crash competed for space with LLM exchanges, which are large and constant: a
 * real export came back 293 traffic entries to 7 errors, and the crash that prompted it survived only by
 * being the most recent thing that happened. Anything a user reproduces, then pokes at before exporting,
 * is gone.
 *
 * Small cap, because errors are rare and each is tiny — this costs nothing and is the difference between
 * a diagnosable report and "the log had nothing in it".
 */
const ERROR_CAP = 50;

let entries: DebugEntry[] = [];
let errorEntries: DebugEntry[] = [];
let enabled = true; // default ON (mirrors setting, overwritten by initDebugLog)
let ready = false;

/** Defensive scrub of anything that looks like a secret in free text (exchanges are pre-redacted). */
function redact(s: string): string {
  return s
    .replace(/(Bearer\s+)[A-Za-z0-9._\-]+/gi, "$1***redacted***")
    .replace(/\b(sk|xai|nvapi|gsk|or)([-_])[A-Za-z0-9._\-]{8,}/g, "$1$2***redacted***") // Groq keys use gsk_ (underscore)
    .replace(/([?&](?:api[_-]?key|key|access_token|token)=)[^&\s]+/gi, "$1***redacted***")
    .replace(/("?(?:api[_-]?key|apikey|authorization)"?\s*[:=]\s*"?)[^"\s,}]+/gi, "$1***redacted***");
}

let saveTimer: ReturnType<typeof setTimeout> | null = null;
function scheduleSave(): void {
  // `debug_entries` holds raw exchanges — prompts, replies, and error text whose key-scrubbing is
  // defensive rather than guaranteed — so it is refused remotely on purpose. Don't keep asking: the
  // in-memory ring still works, which is what an export from this session actually reads.
  if (isRemote()) return;
  if (saveTimer) clearTimeout(saveTimer);
  // Debounced so a burst of entries (streaming a reply) triggers one encrypted write, not hundreds.
  saveTimer = setTimeout(() => {
    saveTimer = null;
    entries = trimToBudget(entries);
    errorEntries = trimToBudget(errorEntries);
    // v2 shape: the error reserve is persisted ALONGSIDE the traffic ring rather than being recovered
    // from it. Rebuilding it by filtering `entries` meant the reserve only ever held errors that had
    // survived the traffic budget — i.e. it did nothing across a restart, which is when crashes are
    // actually reported. Errors are tiny; this costs kilobytes and is the whole point of the log.
    void settingsSet(ENTRIES_KEY, JSON.stringify({ v: 2, entries, errors: errorEntries })).catch(() => {});
  }, 1500);
}

function push(e: DebugEntry): void {
  if (!enabled) return;
  entries.push(e);
  if (entries.length > CAP) entries.splice(0, entries.length - CAP);
  // Errors go in BOTH rings: in place, so the timeline still reads correctly, and in the reserve, so
  // they survive however much traffic follows.
  if (e.kind === "error") {
    errorEntries.push(e);
    if (errorEntries.length > ERROR_CAP) errorEntries.splice(0, errorEntries.length - ERROR_CAP);
  }
  scheduleSave();
}

/** Set the operation label for subsequent LLM exchanges. Call at the start of an action, right before
 *  streaming. Delegates to the inference layer, which stamps it onto each exchange AT CREATION so two
 *  chats generating at once can't relabel each other's exchanges. */
export function setDebugContext(label: string): void {
  setExchangeContext(label);
}

export function logError(label: string, detail: unknown): void {
  push({ t: Date.now(), kind: "error", label, detail: redact(String(detail)) });
}
export function logInfo(label: string, detail?: string): void {
  push({ t: Date.now(), kind: "info", label, detail: detail ? redact(detail) : undefined });
}

export function isDebugEnabled(): boolean {
  return enabled;
}

/** Toggle recording. Persists the flag. Existing entries are kept (so they can still be exported). */
export async function setDebugEnabled(on: boolean): Promise<void> {
  enabled = on;
  await settingsSet(ENABLED_KEY, JSON.stringify(on)).catch(() => {});
}

/** Wipe the log (memory + persisted) — the error reserve too: "Clear the log" that quietly keeps
 *  errors isn't clear, and writing the bare-array v1 shape here corrupted the v2 row. */
export async function clearDebugLog(): Promise<void> {
  entries = [];
  errorEntries = [];
  await settingsSet(ENTRIES_KEY, JSON.stringify({ v: 2, entries: [], errors: [] })).catch(() => {});
}

export function debugEntryCount(): number {
  return entries.length;
}

/** Load persisted state and register the global exchange sink. Idempotent; call once on boot. */
export async function initDebugLog(): Promise<void> {
  if (ready) return;
  ready = true;
  try {
    const rawEnabled = await settingsGet(ENABLED_KEY);
    enabled = rawEnabled == null ? true : JSON.parse(rawEnabled) === true;
  } catch { enabled = true; }
  try {
    const rawEntries = await settingsGet(ENTRIES_KEY);
    const parsed: unknown = rawEntries ? JSON.parse(rawEntries) : [];
    // Two shapes have shipped: a bare array (v1) and { v: 2, entries, errors }. A v1 row is read for its
    // traffic and its errors are recovered the old way — the best that row can offer — and the next save
    // rewrites it as v2.
    const v2 = !Array.isArray(parsed) && typeof parsed === "object" && parsed !== null
      ? (parsed as { entries?: unknown; errors?: unknown })
      : null;
    const rawList: unknown = v2 ? v2.entries : parsed;
    const list: DebugEntry[] = Array.isArray(rawList) ? (rawList as DebugEntry[]) : [];
    // Trim on LOAD as well as on save: a vault written before the budget existed carries a row that would
    // otherwise only shrink on the next exchange, and the first thing this does is re-persist it.
    entries = trimToBudget(list);
    const rawErrors = v2 && Array.isArray(v2.errors) ? (v2.errors as DebugEntry[]) : list.filter((e) => e?.kind === "error");
    errorEntries = trimToBudget(rawErrors).slice(-ERROR_CAP);
    if (!v2 || entries.length < list.length) scheduleSave(); // upgrade the row / persist the trim
  } catch { entries = []; errorEntries = []; }
  // Every completed exchange (from any component) flows here — the reliable capture path.
  onExchangeComplete((ex) => {
    const label = ex.context ?? "chat"; // captured at request start (concurrent-safe), not read now
    push({ t: Date.now(), kind: "llm", label, exchange: ex });
    if (ex.error) push({ t: Date.now(), kind: "error", label: `${label} (llm error)`, detail: redact(ex.error) });
  });
}

/** Write the log to Documents/aicc-chat/exports and return the path. `meta` is merged into the header. */
/** The export's contents, without writing a file — so what a report will actually contain is testable. */
export function debugLogPayload(meta?: Record<string, unknown>, keep: (e: DebugEntry) => boolean = () => true): Record<string, unknown> {
  const kept = entries.filter(keep);
  const errs = errorEntries.filter(keep);
  return {
    app: "Rin Chat",
    exportedAt: new Date().toISOString(),
    userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
    enabled,
    entryCount: kept.length,
    ...meta,
    // FIRST in the file, on purpose. The last export looked empty to the person who sent it — the crash
    // was in there, as entry 7 of 300, under a wall of LLM traffic nobody scrolls through. Whoever opens
    // this should see what went wrong without searching for it.
    errorCount: errs.length,
    errors: errs.map((e) => ({ at: new Date(e.t).toISOString(), label: e.label, detail: e.detail })),
    entries: kept,
  };
}

/** `keep` narrows the export (one Card Forge session); `name` prefixes the file. */
export async function exportDebugLog(meta?: Record<string, unknown>, keep?: (e: DebugEntry) => boolean, name = "aicc-debug"): Promise<string> {
  const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
  return saveExport(`${name}-${stamp}.json`, JSON.stringify(debugLogPayload(meta, keep), null, 2));
}
